import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import LoginModal from '../LoginModal';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Briefcase, MapPin, Clock, Building2, Search, Filter,
  DollarSign, Users, ChevronRight, Plus, X, Check,
  Laptop, Home, Building
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [newJob, setNewJob] = useState({
    job_title: '',
    job_location: '',
    job_type: 'onsite',
    job_salary: '',
    content: ''
  });
  const [isPosting, setIsPosting] = useState(false);

  useEffect(() => {
    if (isAuthenticated && currentUser?.id) {
      fetchUserProfile(currentUser.id);
    }
    fetchJobs();
  }, [currentUser, isAuthenticated]);

  const fetchUserProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/connect/profiles/user/${userId}`);
      if (response.data.exists) {
        setUserProfile(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar perfil do usuário:', error);
    }
  };

  const fetchJobs = async (search = '', type = '') => {
    try {
      let url = `${API}/connect/jobs?limit=20`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (type) url += `&job_type=${type}`;
      
      const response = await axios.get(url);
      setJobs(response.data);
    } catch (error) {
      console.error('Erro ao carregar vagas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs(searchQuery, filterType);
  };

  const handleCreateJob = async () => {
    if (!newJob.job_title || !newJob.content) {
      toast.error('Preencha o título e descrição da vaga');
      return;
    }

    if (!userProfile?.profile_id) {
      toast.error('Crie seu perfil primeiro');
      navigate('/connect/create-profile');
      return;
    }

    setIsPosting(true);
    try {
      await axios.post(`${API}/connect/jobs?author_id=${userProfile.profile_id}`, {
        ...newJob,
        post_type: 'job'
      });
      toast.success('Vaga publicada com sucesso!');
      setShowCreateJob(false);
      setNewJob({ job_title: '', job_location: '', job_type: 'onsite', job_salary: '', content: '' });
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao publicar vaga');
    } finally {
      setIsPosting(false);
    }
  };

  const handleApply = async (jobId) => {
    if (!userProfile?.profile_id) {
      toast.error('Crie seu perfil para se candidatar');
      navigate('/connect/create-profile');
      return;
    }

    try {
      await axios.post(`${API}/connect/jobs/${jobId}/apply?profile_id=${userProfile.profile_id}`);
      toast.success('Candidatura enviada!');
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao se candidatar');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Hoje';
    if (days === 1) return 'Ontem';
    if (days < 7) return `${days} dias atrás`;
    if (days < 30) return `${Math.floor(days / 7)} semanas atrás`;
    return date.toLocaleDateString('pt-BR');
  };

  const getJobTypeIcon = (type) => {
    switch (type) {
      case 'remote': return <Laptop className="w-4 h-4" />;
      case 'onsite': return <Building className="w-4 h-4" />;
      case 'hybrid': return <Home className="w-4 h-4" />;
      default: return <Building className="w-4 h-4" />;
    }
  };

  const getJobTypeLabel = (type) => {
    switch (type) {
      case 'remote': return 'Remoto';
      case 'onsite': return 'Presencial';
      case 'hybrid': return 'Híbrido';
      default: return type;
    }
  };

  const canPostJobs = userProfile?.plan === 'company';

  return (
    <div className="min-h-screen bg-black">
      <Header onLoginClick={() => setIsLoginModalOpen(true)} />
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      
      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-gradient-to-b from-green-900/20 via-zinc-900 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-green-600/20 text-green-400 border-green-500/30 mb-4">
              <Briefcase className="w-4 h-4 mr-2" />
              Vagas BIM
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Encontre sua próxima <span className="text-green-500">oportunidade</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8">
              As melhores vagas do mercado BIM em um só lugar
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="flex gap-2 bg-zinc-800/80 p-2 rounded-xl border border-zinc-700">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar vagas..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-transparent border-0 text-white placeholder-gray-500 focus:ring-0"
                    data-testid="jobs-search-input"
                  />
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="bg-zinc-700 text-white rounded-lg px-4 py-2 border-0 focus:ring-2 focus:ring-green-500"
                  data-testid="jobs-filter-type"
                >
                  <option value="">Todos os tipos</option>
                  <option value="remote">Remoto</option>
                  <option value="onsite">Presencial</option>
                  <option value="hybrid">Híbrido</option>
                </select>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="jobs-search-btn">
                  <Search className="w-5 h-5" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA for Companies */}
      {isAuthenticated && canPostJobs && (
        <section className="py-4 bg-gradient-to-r from-green-600 to-green-800">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <h2 className="text-xl font-bold text-white">Encontre os melhores talentos BIM!</h2>
                <p className="text-green-100">Publique suas vagas e receba candidaturas qualificadas.</p>
              </div>
              <Button 
                onClick={() => setShowCreateJob(true)}
                className="bg-white text-green-600 hover:bg-green-50 px-8"
                data-testid="create-job-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Publicar Vaga
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Create Job Modal */}
      {showCreateJob && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-zinc-900 border-zinc-800 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <h2 className="text-xl font-bold text-white">Nova Vaga</h2>
              <button onClick={() => setShowCreateJob(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Título da Vaga *</label>
                <Input
                  value={newJob.job_title}
                  onChange={(e) => setNewJob({ ...newJob, job_title: e.target.value })}
                  placeholder="Ex: BIM Manager, Modelador Revit..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="job-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Localização</label>
                  <Input
                    value={newJob.job_location}
                    onChange={(e) => setNewJob({ ...newJob, job_location: e.target.value })}
                    placeholder="São Paulo, SP"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Tipo</label>
                  <select
                    value={newJob.job_type}
                    onChange={(e) => setNewJob({ ...newJob, job_type: e.target.value })}
                    className="w-full bg-zinc-800 text-white rounded-md px-3 py-2 border border-zinc-700"
                  >
                    <option value="onsite">Presencial</option>
                    <option value="remote">Remoto</option>
                    <option value="hybrid">Híbrido</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Faixa Salarial</label>
                <Input
                  value={newJob.job_salary}
                  onChange={(e) => setNewJob({ ...newJob, job_salary: e.target.value })}
                  placeholder="Ex: R$ 5.000 - R$ 8.000 ou A combinar"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Descrição da Vaga *</label>
                <textarea
                  value={newJob.content}
                  onChange={(e) => setNewJob({ ...newJob, content: e.target.value })}
                  placeholder="Descreva a vaga, requisitos, benefícios..."
                  rows={6}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                  data-testid="job-description-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateJob(false)} className="border-zinc-700 text-gray-300">
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCreateJob}
                  disabled={isPosting}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="publish-job-btn"
                >
                  {isPosting ? 'Publicando...' : 'Publicar Vaga'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Jobs List */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-400 mt-4">Carregando vagas...</p>
            </div>
          ) : jobs.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-12 text-center">
                <Briefcase className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Nenhuma vaga encontrada</h3>
                <p className="text-gray-400 mb-6">Seja o primeiro a publicar uma vaga!</p>
                {canPostJobs && (
                  <Button 
                    onClick={() => setShowCreateJob(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Publicar Vaga
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="bg-zinc-900 border-zinc-800 hover:border-green-500/50 transition-all cursor-pointer group"
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Company Logo */}
                      <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                        {job.company_photo ? (
                          <img src={job.company_photo} alt={job.company} className="w-full h-full rounded-lg object-cover" />
                        ) : (
                          job.company?.charAt(0)?.toUpperCase()
                        )}
                      </div>

                      {/* Job Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-semibold text-white group-hover:text-green-400 transition-colors">
                              {job.title}
                            </h3>
                            <p className="text-gray-400">{job.company}</p>
                          </div>
                          <Badge className="bg-green-600/20 text-green-400 flex-shrink-0">
                            {getJobTypeIcon(job.type)}
                            <span className="ml-1">{getJobTypeLabel(job.type)}</span>
                          </Badge>
                        </div>

                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
                          {job.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                          )}
                          {job.salary && (
                            <span className="flex items-center gap-1 text-green-400">
                              <DollarSign className="w-4 h-4" />
                              {job.salary}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(job.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {job.applications_count} candidaturas
                          </span>
                        </div>

                        <p className="text-gray-400 mt-3 line-clamp-2">{job.description}</p>

                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
                          <Button 
                            variant="ghost" 
                            className="text-gray-400 hover:text-white"
                            onClick={() => navigate(`/connect/jobs/${job.id}`)}
                          >
                            Ver detalhes
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                          <Button 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApply(job.id);
                            }}
                            data-testid={`apply-btn-${job.id}`}
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Candidatar-se
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JobsPage;
